import { Cesium } from 'app/cesium';
import { PolygonValueType } from 'app/form_config/helpers';
import { AssetCollection } from 'app/shared/collections/asset_collection.service';
import { ImsObject } from 'app/shared/collections/ims_object';
import { STAT_DEFINITION__PARENT_TYPE_IP_DEVICE } from 'app/shared/collections/StatDefinition';
import {
    ViewConnectionLineSchema,
    ViewIndicatorSchema,
    ViewLabelSchema,
    ViewLineVisualizerSchema,
    ViewPolygonVisualizerSchema,
    ViewTagBasedIndicatorSchema,
    ViewVectorSchema,
} from 'app/shared/collections/view';
import { ImageUrlService } from 'app/shared/image_url.service';
import { ImsItemTypeDetails, ImsItemTypeRegistry } from 'app/shared/ims_items/ImsItemTypeRegistry';
import { MapOptionsService } from 'app/shared/map_options/MapOptionsService';
import { ClockHandler } from 'app/shared/time/clock_handler.service';
import ColorRenderer from 'app/shared/utilities/color_renderer';
import {
    ConnectionLineModel,
    IconIndicatorModel,
    LabelModel,
    LineModel,
    PolygonModel,
    VectorModel,
} from 'app/views/accessories/IndicatorManager';
import { StatBasedIndicatorManager } from 'app/views/accessories/StatBasedIndicatorManager';
import { getIndicatorMapOptionName, resolveIconUrl } from 'app/views/view_types/view_utils';
import { isNil } from 'lodash';
import { SelectionService } from 'app/shared/selection/SelectionService';
import { SelectionType } from 'app/shared/selection/SelectionType';

export class IndicatorCollectionFactoryForViewConfigs {
    private readonly _imsItemTypeRegistry: ImsItemTypeRegistry;
    private readonly _assetCollection: AssetCollection;
    private readonly _clockHandler: ClockHandler;
    private readonly _assetOptionsService: MapOptionsService;
    private readonly _imageUrlService: ImageUrlService;
    private _selectionService: SelectionService;

    /**
     * This factory has public methods which accept different view component configs, and returns
     *  relevant {@link IndicatorManager} instances.
     */
    constructor(
        imsItemTypeRegistry: ImsItemTypeRegistry,
        assetCollection: AssetCollection,
        clockHandler: ClockHandler,
        assetOptionsService: MapOptionsService,
        imageUrlService: ImageUrlService,
        selectionService: SelectionService,
    ) {
        this._imsItemTypeRegistry = imsItemTypeRegistry;
        this._assetCollection = assetCollection;
        this._clockHandler = clockHandler;
        this._assetOptionsService = assetOptionsService;
        this._imageUrlService = imageUrlService;
        this._selectionService = selectionService;
    }

    private _getDetails(
        config:
            | ViewVectorSchema
            | ViewIndicatorSchema
            | ViewTagBasedIndicatorSchema
            | ViewLabelSchema
            | ViewConnectionLineSchema
            | ViewPolygonVisualizerSchema
            | ViewLineVisualizerSchema,
    ) {
        const tableToRead = config.table
            ? config.table
            : config.type === 'tag_based_indicator'
              ? 'location_stat'
              : undefined;
        const typeDetails = this._imsItemTypeRegistry.getTypeDetails(tableToRead);
        if (!typeDetails) {
            throw new Error(
                `Error construction indicator manager ${config.display_name}. Could not find IMS type details for table ${tableToRead}`,
            );
        }
        const isForIpDevice =
            typeDetails.statDefinition.parent_type === STAT_DEFINITION__PARENT_TYPE_IP_DEVICE;

        // create an observer for this particular indicator's map option
        // this is just a nicer interface than listening to the asset options service
        const mapOptionName = getIndicatorMapOptionName(config);
        const optionObserver = this._assetOptionsService.createObservableForValue(mapOptionName);
        return {
            typeDetails,
            isForIpDevice,
            optionObserver,
        };
    }

    public createFromLabelConfig(config: ViewLabelSchema): StatBasedIndicatorManager<LabelModel> {
        const { typeDetails, isForIpDevice, optionObserver } = this._getDetails(config);
        const createLabel = createLabelCreateFn(config);
        return new StatBasedIndicatorManager<LabelModel>(
            this._assetCollection,
            typeDetails.collection,
            this._clockHandler,
            optionObserver,
            isForIpDevice,
            createLabel,
            this._imsItemTypeRegistry,
            this._selectionService,
        );
    }

    public createFromVectorConfig(
        config: ViewVectorSchema,
    ): StatBasedIndicatorManager<VectorModel> {
        const { typeDetails, isForIpDevice, optionObserver } = this._getDetails(config);
        const colorRenderer = getColorRendererFromViewConfig(config, typeDetails);
        const createVector = createVectorCreateFn(config, colorRenderer);
        return new StatBasedIndicatorManager<VectorModel>(
            this._assetCollection,
            typeDetails.collection,
            this._clockHandler,
            optionObserver,
            isForIpDevice,
            createVector,
            this._imsItemTypeRegistry,
            this._selectionService,
        );
    }

    public createFromConnectionLineConfig(
        config: ViewConnectionLineSchema,
    ): StatBasedIndicatorManager<ConnectionLineModel> {
        const { typeDetails, isForIpDevice, optionObserver } = this._getDetails(config);
        const colorRenderer = getColorRendererFromViewConfig(config, typeDetails);
        const createConnectionLine = createConnectionLineCreateFn(config, colorRenderer);
        return new StatBasedIndicatorManager<ConnectionLineModel>(
            this._assetCollection,
            typeDetails.collection,
            this._clockHandler,
            optionObserver,
            isForIpDevice,
            createConnectionLine,
            this._imsItemTypeRegistry,
            this._selectionService,
        );
    }

    public createFromPolygonVisualizerConfig(
        config: ViewPolygonVisualizerSchema,
    ): StatBasedIndicatorManager<PolygonModel> {
        const { typeDetails, isForIpDevice, optionObserver } = this._getDetails(config);
        const colorRenderer = getColorRendererFromViewConfig(config, typeDetails);
        const createPolygon = createPolygonVisualizerCreateFn(config, colorRenderer);
        return new StatBasedIndicatorManager<PolygonModel>(
            this._assetCollection,
            typeDetails.collection,
            this._clockHandler,
            optionObserver,
            isForIpDevice,
            createPolygon,
            this._imsItemTypeRegistry,
            this._selectionService,
        );
    }

    public createFromLineVisualizerConfig(
        config: ViewLineVisualizerSchema,
    ): StatBasedIndicatorManager<LineModel> {
        const { typeDetails, isForIpDevice, optionObserver } = this._getDetails(config);
        const colorRenderer = getColorRendererFromViewConfig(config, typeDetails);
        const createPolyLine = createPolyLineCreateFn(config, colorRenderer);
        return new StatBasedIndicatorManager<LineModel>(
            this._assetCollection,
            typeDetails.collection,
            this._clockHandler,
            optionObserver,
            isForIpDevice,
            createPolyLine,
            this._imsItemTypeRegistry,
            this._selectionService,
        );
    }

    public createFromIndicatorConfig(
        config: ViewIndicatorSchema | ViewTagBasedIndicatorSchema,
    ): StatBasedIndicatorManager<IconIndicatorModel> {
        const { typeDetails, isForIpDevice, optionObserver } = this._getDetails(config);
        let createIndicatorFn;

        if (config.type === 'tag_based_indicator') {
            createIndicatorFn = createTagBasedIndicatorCreateFn(config, this._imageUrlService);
        } else {
            createIndicatorFn = createIndicatorCreateFn(
                config,
                this._imageUrlService,
                this._assetCollection,
                this._selectionService,
            );
        }

        return new StatBasedIndicatorManager<IconIndicatorModel>(
            this._assetCollection,
            typeDetails.collection,
            this._clockHandler,
            optionObserver,
            isForIpDevice,
            createIndicatorFn,
            this._imsItemTypeRegistry,
            this._selectionService,
            config,
        );
    }
}

function getColorRendererFromViewConfig(
    config:
        | ViewVectorSchema
        | ViewConnectionLineSchema
        | ViewPolygonVisualizerSchema
        | ViewLineVisualizerSchema,
    typeDetails: ImsItemTypeDetails,
): ColorRenderer {
    let colorConfig;
    if (config.color_config_field) {
        colorConfig =
            typeDetails.statDefinition.field_schemas[config.color_config_field]?.color_config;
    }
    if (!colorConfig && config.color) {
        colorConfig = new ColorRenderer(config.color);
    }
    if (!colorConfig) {
        colorConfig = {
            type: 'exact',
            default: [255, 255, 255, 1],
            points: [{ value: 0, color: [255, 255, 255, 1] }],
        };
    }
    return new ColorRenderer(colorConfig);
}

function createGetterFn<T>(
    parameterName: string,
    expr: string | number | null,
    defaultValue: number | null,
): (value: T) => number | null {
    if (typeof expr === 'string') {
        const fn = new Function(parameterName, `return ${expr}`) as (value: T) => number;
        // who knows what's in this expression, it will probably explode
        return (x) => {
            try {
                return fn(x);
            } catch {
                return null;
            }
        };
    }
    if (Number.isFinite(expr)) {
        return () => expr;
    }
    return () => defaultValue;
}

function createLabelCreateFn(
    config: ViewLabelSchema,
): (stats: Array<ImsObject>) => Array<LabelModel> {
    const getTextFn = createGetterFn<Array<ImsObject>>('stats', config.text_expression, null);
    return (stats: Array<ImsObject>) => {
        const text = getTextFn(stats);
        return [
            {
                type: 'label',
                text: `${text}`,
            },
        ];
    };
}

function createConnectionLineCreateFn(
    config: ViewConnectionLineSchema,
    colorRenderer: ColorRenderer,
): (stats: Array<ImsObject>) => Array<ConnectionLineModel> {
    return (stats: Array<ImsObject>) => {
        return (stats || []).reduce((connectionLines, stat) => {
            const colorFieldValue = stat[config.color_config_field];
            const color = colorRenderer.getColor(colorFieldValue);
            const foreignAsset = stat[config.dest_asset_field];

            // if ANY of these values are nil, we have no vector to draw
            if ([foreignAsset, color].some(isNil)) {
                return connectionLines;
            }
            connectionLines.push({
                type: 'connection-line',
                color,
                width: 3,
                destinationAssetId: foreignAsset,
            } as ConnectionLineModel);
            return connectionLines;
        }, []);
    };
}

function createPolyLineCreateFn(
    config: ViewLineVisualizerSchema,
    colorRenderer: ColorRenderer,
): (stats: Array<ImsObject>) => Array<LineModel> {
    return (stats: Array<ImsObject>) => {
        return (stats || []).reduce((lines, stat) => {
            const colorFieldValue = stat[config.color_config_field];
            const color = colorRenderer.getColor(colorFieldValue);
            const lineStateValue = stat[config.line_field];

            // if ANY of these values are nil, we have no vector to draw
            if ([lineStateValue, color].some(isNil)) {
                return lines;
            }

            const line = latLonArrayToCartesian3Array(lineStateValue);
            lines.push({
                type: 'linestring',
                color,
                line: line,
                width: 4,
            } as LineModel);
            return lines;
        }, []);
    };
}

function latLonArrayToCartesian3Array(input: PolygonValueType): Array<Cesium.Cartesian3> {
    return input?.map(([longitude, latitude]) =>
        Cesium.Cartesian3.fromDegrees(longitude, latitude),
    );
}

function createPolygonVisualizerCreateFn(
    config: ViewPolygonVisualizerSchema,
    colorRenderer: ColorRenderer,
): (stats: Array<ImsObject>) => Array<PolygonModel> {
    return (stats: Array<ImsObject>) => {
        return (stats || []).reduce((polygonModels, stat) => {
            const colorFieldValue = stat[config.color_config_field];
            const color = colorRenderer.getColor(colorFieldValue);
            const polygonStateValue = stat[config.polygon_field];

            // if ANY of these values are nil, we have no vector to draw
            if ([polygonStateValue, color].some(isNil)) {
                return polygonModels;
            }

            const polygon = latLonArrayToCartesian3Array(polygonStateValue);
            polygonModels.push({
                type: 'polygon',
                color,
                polygon,
            } as PolygonModel);
            return polygonModels;
        }, []);
    };
}

function createVectorCreateFn(
    config: ViewVectorSchema,
    colorRenderer: ColorRenderer,
): (stats: Array<ImsObject>) => Array<VectorModel> {
    const getHeadingFn = createGetterFn<ImsObject>('stat', config.heading_expr, 0);
    const getPitchFn = createGetterFn<ImsObject>('stat', config.pitch_expr, 0);
    const getLengthFn = createGetterFn<ImsObject>('stat', config.length_expr, 10);
    const getWidthFn = createGetterFn<ImsObject>('stat', config.width_expr, 10);

    return (stats: Array<ImsObject>) => {
        return (stats || []).reduce((vectors, stat) => {
            const heading = getHeadingFn(stat);
            const pitch = getPitchFn(stat);
            const length = getLengthFn(stat);
            const width = getWidthFn(stat);
            const color = colorRenderer.getColor(length);

            // if ANY of these values are nil, we have no vector to draw
            if ([heading, pitch, length, color, width].some(isNil)) {
                return vectors;
            }
            vectors.push({
                type: 'vector',
                color,
                length,
                heading,
                pitch,
                width,
            });
            return vectors;
        }, []);
    };
}

function createIndicatorCreateFn(
    config: ViewIndicatorSchema,
    imageUrlService: ImageUrlService,
    assetCollection: AssetCollection,
    selectionService: SelectionService,
): (stats: Array<ImsObject>) => Array<IconIndicatorModel> {
    if (!config.indicator_per_stat) {
        // A function to call which returns the current value of an indicator based on an assets current list of stats
        const getValueFn = createGetterFn<Array<ImsObject>>('stats', config.value_expression, null);
        // A function to call which returns the current text of an indicator based on an assets current list of stats
        const getTextFn = createGetterFn<Array<ImsObject>>('stats', config.text_expression, null);
        // A color renderer used to produce the colour of an indicator based on the return value of the value function
        const colorRenderer = new ColorRenderer(config.color);
        const iconUrl = resolveIconUrl(config.icon, imageUrlService);

        return (stats: Array<ImsObject>) => {
            const value = getValueFn(stats);
            if (value !== null && value !== undefined) {
                const text = getTextFn(stats);
                const color = colorRenderer.getColor(value);
                return [
                    {
                        type: 'indicator',
                        color,
                        text: `${text}`,
                        icon: iconUrl,
                    },
                ];
            }
            return [];
        };
    } else {
        // A function to call which returns the current value of an indicator based on an asset current list of stats
        const getValueFn = createGetterFn<ImsObject>('stat', config.value_expression, null);
        // A function to call which returns the current text of an indicator based on an asset current list of stats
        const getTextFn = createGetterFn<ImsObject>('stat', config.text_expression, null);
        // A function to call which returns the current text of an indicator based on an asset current list of stats
        const getAssetIdTextFn = createGetterFn<ImsObject>(
            'stat',
            config.asset_id_expression,
            null,
        );
        // A colour renderer used to produce the colour of an indicator based on the return value of the value function
        const colorRenderer = new ColorRenderer(config.color);
        const iconUrl = resolveIconUrl(config.icon, imageUrlService);
        return (stats: Array<ImsObject>) => {
            return stats.reduce((acc: IconIndicatorModel[], stat) => {
                const value = getValueFn(stat);
                const assetId = getAssetIdTextFn(stat);
                const isSelected = selectionService.isSelected(assetId, SelectionType.ASSET);

                if (value !== null && value !== undefined) {
                    const text = getTextFn(stat);
                    const color = colorRenderer.getColor(value);
                    acc.push({
                        type: 'indicator',
                        color,
                        text: `${text}`,
                        icon: iconUrl,
                        asset: assetCollection.get(assetId),
                        isSelected,
                    });
                }
                return acc;
            }, []);
            return [];
        };
    }
}

function createTagBasedIndicatorCreateFn(
    config: ViewTagBasedIndicatorSchema,
    imageUrlService: ImageUrlService,
): (stats: Array<ImsObject>) => Array<IconIndicatorModel> {
    // A function to call which returns the current value of an indicator based on an asset current list of stats
    const value_expression = `((locationStats) => { if (!locationStats || locationStats.length === 0) { return null; } let assetId = locationStats[0].assetId; if (!assetId) { return null; } let tags = Object.values(window.services.assetCollection.get(assetId)?.tags) || []; let result = tags.some(t => t.name.toLowerCase() === '${config.tag}'.toLowerCase()); return result ? 1 : null; })(stats)`;
    const getValueFn = createGetterFn<Array<ImsObject>>('stats', value_expression, null);
    // A colour renderer used to produce the colour of an indicator based on the return value of the value function
    const colorRenderer = new ColorRenderer(config.color);
    const iconUrl = resolveIconUrl(config.icon, imageUrlService);

    return (stats: Array<ImsObject>) => {
        const value = getValueFn(stats);
        if (value !== null && value !== undefined) {
            const text = config.text ? config.text : config.tag;
            const color = colorRenderer.getColor(value);
            return [
                {
                    type: 'indicator',
                    color,
                    text: `${text}`,
                    icon: iconUrl,
                },
            ];
        }
        return [];
    };
}
